/**
 * Algolia AI Search & Discovery custom hooks for the Webster theme.
 *
 * ## Required Shopify / Algolia admin (not stored in theme code)
 *
 * 1. Algolia AI Search & Discovery app installed; Credentials page saved
 *    (Application ID, keys, index prefix e.g. `shopify_`).
 * 2. Product indexing complete in Algolia dashboard.
 * 3. Theme customizer → Embed apps → Algolia Search → enabled.
 * 4. App admin → Search options → CSS selector (use one; do NOT use form[action="/search"]):
 *
 *      #search-modal #algolia-autocomplete-input
 *
 *    Fallback if the app requires a form selector:
 *
 *      #search-modal #search-modal-search-form
 *
 *    Avoid `#search-modal form[action="/search"]` or bare `form[action="/search"]` — those
 *    match overly broad targets and can wipe modal markup when Autocomplete initializes.
 *
 * 5. Search template: native search sections disabled; InstantSearch app block present.
 *
 * Query suggestions while typing: enabled when the Query Suggestions index responds
 * (`{index_prefix}products_query_suggestions`). Create that index in Algolia dashboard and
 * enable Query Suggestions in the Algolia app Search options. If the index is missing, the
 * theme keeps suggestions disabled to avoid 404 errors on every keystroke.
 *
 * Popular search terms (empty state): shop metafield `custom.popular_search_terms`, with Algolia
 * index fallback via fetchPopularSearchQueries() when the metafield is empty.
 *
 * ## Storefront verification
 *
 * - `typeof algoliaShopify !== 'undefined'`
 * - Open header search → popular terms from shop metafield, or Algolia fallback if metafield empty
 * - Empty input → theme empty state; type query → suggestions, collections, products in `.aa-Panel`
 *
 * Search modal typing-results layout (Figma mobile + desktop): custom section headings, text
 * suggestion/designer rows, product cards, and See All footer via Algolia template hooks.
 * Enable **Show See All products** in Algolia app → Search options → Autocomplete for the footer CTA.
 *
 * Detached mode is disabled for the search modal (`detachedMediaQuery: 'none'`) so mobile
 * renders an inline `.aa-Input` inside the modal instead of `.aa-DetachedSearchButton`.
 */

/** @type {string[] | null} */
let cachedPopularSearchQueries = null;

/** @type {Promise<string[]> | null} */
let popularSearchFetchPromise = null;

/** @type {boolean | null} */
let querySuggestionsIndexAvailable = null;

/** @type {Promise<boolean> | null} */
let querySuggestionsProbePromise = null;

const POPULAR_SEARCHES_LIMIT = 6;
const AUTOCOMPLETE_HITS_PER_PAGE_MOBILE = 5;
const AUTOCOMPLETE_HITS_PER_PAGE_DESKTOP = 4;
const INSTANTSEARCH_HITS_PER_PAGE = 24;

/** @type {boolean} */
let instantSearchLoadMoreInitialized = false;

/** @type {boolean | null} */
let instantSearchInfiniteHitsAvailable = null;

/** @type {HTMLElement[]} */
let preservedInstantSearchHitItems = [];

/** @type {string | null} */
let preservedInstantSearchStateKey = null;

/**
 * @returns {boolean}
 */
function isInstantSearchInfiniteHitsAvailable() {
  if (instantSearchInfiniteHitsAvailable !== null) {
    return instantSearchInfiniteHitsAvailable;
  }

  // @ts-expect-error Algolia app injects this global at runtime
  const infiniteHitsWidget = algoliaShopify?.externals?.widgets?.infiniteHits;
  instantSearchInfiniteHitsAvailable =
    typeof infiniteHitsWidget === 'function';

  return instantSearchInfiniteHitsAvailable;
}

/** @type {{ suggestions: string, designers: string, products: string, seeAllTemplate: string } | null} */
let cachedSearchModalLabels = null;

/**
 * @returns {{ suggestions: string, designers: string, products: string, seeAllTemplate: string }}
 */
function getSearchModalLabels() {
  if (cachedSearchModalLabels) return cachedSearchModalLabels;

  const component = document.querySelector('search-modal-component');
  const el = component instanceof HTMLElement ? component : null;
  cachedSearchModalLabels = {
    suggestions:
      el?.dataset.searchHeadingSuggestions?.trim() || 'Top Suggestions',
    designers: el?.dataset.searchHeadingDesigners?.trim() || 'Designers',
    products: el?.dataset.searchHeadingProducts?.trim() || 'Suggested Products',
    seeAllTemplate:
      el?.dataset.searchSeeAllTemplate?.trim() || 'See all {{ count }} results',
  };

  return cachedSearchModalLabels;
}

/**
 * @param {string} resource
 * @returns {string | null}
 */
function resolveAutocompleteSectionHeading(resource) {
  const labels = getSearchModalLabels();
  const id = normalizeAutocompleteResource(resource);

  if (id === 'querySuggestionsPlugin') return labels.suggestions;
  if (id === 'collections') return labels.designers;
  if (id === 'products') return labels.products;

  return null;
}

/**
 * @param {unknown} resource
 * @param {{ resource?: string, sourceId?: string, pluginName?: string } | undefined} [templateOptions]
 * @returns {string}
 */
function normalizeAutocompleteResource(resource, templateOptions) {
  if (typeof resource === 'string') return resource;

  if (resource && typeof resource === 'object') {
    const record = /** @type {Record<string, unknown>} */ (resource);
    const fromObject =
      record.sourceId || record.resource || record.name || record.pluginName;
    if (typeof fromObject === 'string') return fromObject;
  }

  const fromOptions =
    templateOptions?.resource ||
    templateOptions?.sourceId ||
    templateOptions?.pluginName;
  return typeof fromOptions === 'string' ? fromOptions : '';
}

/**
 * @param {Function} html
 * @param {string} resource
 * @returns {unknown}
 */
function renderAutocompleteSectionHeader(html, resource) {
  const heading = resolveAutocompleteSectionHeading(resource);

  if (!heading) return null;

  const modifier =
    normalizeAutocompleteResource(resource) === 'querySuggestionsPlugin'
      ? 'search-modal__results-heading--suggestions'
      : 'search-modal__results-heading--default';

  return html`
    <div class="aa-SourceHeader search-modal__results-header">
      <span
        class="aa-SourceHeaderTitle search-modal__results-heading ${modifier}"
      >
        ${heading}
      </span>
    </div>
  `;
}

/**
 * @param {number} count
 * @returns {string}
 */
function formatSeeAllResultsLabel(count) {
  const template = getSearchModalLabels().seeAllTemplate;
  // @ts-expect-error Algolia app injects this global at runtime
  const formatter = algoliaShopify?.helpers?.formatNumber;
  const formattedCount =
    typeof formatter === 'function' ? formatter(count) : String(count);
  return template.replace('{{ count }}', formattedCount);
}

/**
 * @returns {boolean}
 */
function isMobileSearchResults() {
  return window.matchMedia('(max-width: 749px)').matches;
}

/** @type {boolean} */
let searchModalTemplatesEnabled = false;

/**
 * @returns {boolean}
 */
function shouldUseSearchModalTemplates() {
  return searchModalTemplatesEnabled;
}

/**
 * @returns {number}
 */
function getAutocompleteHitsPerPage() {
  return isMobileSearchResults()
    ? AUTOCOMPLETE_HITS_PER_PAGE_MOBILE
    : AUTOCOMPLETE_HITS_PER_PAGE_DESKTOP;
}

/**
 * @param {HTMLElement | null | undefined} searchModal
 * @returns {HTMLInputElement | null}
 */
function getModalSearchInput(searchModal) {
  const aaInput = searchModal?.querySelector(
    '#algolia-autocomplete-input .aa-Input',
  );
  if (aaInput instanceof HTMLInputElement) return aaInput;

  const nativeInput = searchModal?.querySelector('.search-input');
  if (nativeInput instanceof HTMLInputElement) return nativeInput;

  return null;
}

/**
 * @returns {string | null}
 */
function resolveQuerySuggestionsIndexName() {
  // @ts-expect-error Algolia app injects this global at runtime
  if (typeof algoliaShopify === 'undefined') return null;

  // @ts-expect-error Algolia app injects this global at runtime
  const config = algoliaShopify.config;
  if (!config) return null;

  if (
    typeof config.query_suggestions_index_name === 'string' &&
    config.query_suggestions_index_name
  ) {
    return config.query_suggestions_index_name;
  }

  if (
    typeof config.querySuggestionsIndexName === 'string' &&
    config.querySuggestionsIndexName
  ) {
    return config.querySuggestionsIndexName;
  }

  const prefix =
    typeof config.index_prefix === 'string' ? config.index_prefix : 'shopify_';
  return `${prefix}products_query_suggestions`;
}

/**
 * @returns {Promise<boolean>}
 */
async function probeQuerySuggestionsIndex() {
  if (querySuggestionsIndexAvailable !== null) {
    return querySuggestionsIndexAvailable;
  }

  if (querySuggestionsProbePromise) {
    return querySuggestionsProbePromise;
  }

  querySuggestionsProbePromise = (async () => {
    // @ts-expect-error Algolia app injects this global at runtime
    if (typeof algoliaShopify === 'undefined') {
      querySuggestionsIndexAvailable = false;
      return false;
    }

    // @ts-expect-error Algolia app injects this global at runtime
    const { searchClient } = algoliaShopify;
    const indexName = resolveQuerySuggestionsIndexName();

    if (!searchClient || !indexName) {
      querySuggestionsIndexAvailable = false;
      return false;
    }

    try {
      const result = await searchClient.search([
        {
          indexName,
          query: '',
          hitsPerPage: 1,
        },
      ]);

      querySuggestionsIndexAvailable = Array.isArray(result?.results);
      return querySuggestionsIndexAvailable;
    } catch {
      querySuggestionsIndexAvailable = false;
      return false;
    }
  })();

  const available = await querySuggestionsProbePromise;
  querySuggestionsProbePromise = null;
  return available;
}

/**
 * @returns {Promise<string[]>}
 */
async function fetchPopularSearchQueries() {
  if (cachedPopularSearchQueries) {
    return cachedPopularSearchQueries;
  }

  if (popularSearchFetchPromise) {
    return popularSearchFetchPromise;
  }

  popularSearchFetchPromise = (async () => {
    const indexAvailable = await probeQuerySuggestionsIndex();
    if (!indexAvailable) return [];

    // @ts-expect-error Algolia app injects this global at runtime
    if (typeof algoliaShopify === 'undefined') return [];

    // @ts-expect-error Algolia app injects this global at runtime
    const { searchClient } = algoliaShopify;

    const indexName = resolveQuerySuggestionsIndexName();

    if (!searchClient || !indexName) {
      return [];
    }

    try {
      const result = await searchClient.search([
        {
          indexName,
          query: '',
          hitsPerPage: POPULAR_SEARCHES_LIMIT,
        },
      ]);

      const hits = result?.results?.[0]?.hits;
      if (!Array.isArray(hits)) return [];

      /** @type {string[]} */
      const normalizedQueries = [];
      for (const hit of hits) {
        const query = hit.query?.trim();
        if (query) normalizedQueries.push(query);
      }

      cachedPopularSearchQueries = normalizedQueries;
      return normalizedQueries;
    } catch {
      return [];
    }
  })();

  const queries = (await popularSearchFetchPromise) ?? [];
  popularSearchFetchPromise = null;
  return queries;
}

/**
 * @param {string} query
 * @returns {string}
 */
function buildPopularSearchPageUrl(query) {
  // @ts-expect-error Theme routes injected in snippets/scripts.liquid
  const searchPath = window.Theme?.routes?.search_url ?? '/search';
  const url = new URL(searchPath, window.location.origin);
  url.searchParams.set('q', query);
  url.searchParams.set('type', 'product');
  return url.pathname + url.search;
}

/**
 * @param {string[]} queries
 */
function renderPopularSearchQueries(queries) {
  const container = document.querySelector('[data-algolia-popular-searches]');
  const list = document.querySelector('[data-algolia-popular-searches-list]');

  if (
    !(container instanceof HTMLElement) ||
    !(list instanceof HTMLUListElement)
  )
    return;

  list.replaceChildren();

  if (queries.length === 0) {
    container.hidden = true;
    return;
  }

  for (const query of queries) {
    const item = document.createElement('li');
    item.className = 'search-modal__popular-item';

    const link = document.createElement('a');
    link.className = 'search-modal__popular-term';
    link.href = buildPopularSearchPageUrl(query);
    link.textContent = query;

    item.appendChild(link);
    list.appendChild(item);
  }

  container.hidden = false;
}

async function loadPopularSearchSuggestions() {
  const container = document.querySelector('[data-algolia-popular-searches]');
  if (!(container instanceof HTMLElement)) return;

  const queries = await fetchPopularSearchQueries();
  renderPopularSearchQueries(queries);
}

/**
 * @param {Event} event
 * @returns {boolean}
 */
function shouldPreventSearchPanelBlur(event) {
  const target = event.target;
  if (!(target instanceof Element)) return false;

  return Boolean(
    target.closest(
      'a, button, .aa-ItemLink, .aa-ItemWrapper, .aa-SeeAllBtn, [role="option"]',
    ),
  );
}

function preventPanelBlurOnPointerDown() {
  const panel = document.getElementById('algolia-autocomplete-panel');
  if (
    !(panel instanceof HTMLElement) ||
    panel.dataset.searchModalPanelGuard === 'true'
  )
    return;

  panel.dataset.searchModalPanelGuard = 'true';

  // Cancelling mousedown keeps the input focused, so the panel is still open
  // when the click lands. It leaves the click itself alone, and a touch fires
  // an emulated mousedown before its click, so this covers touch as well.
  //
  // It has to stay mousedown. The same call on touchend suppresses the whole
  // emulated mouse sequence the browser sends after a tap, click included, so
  // tapping a result did nothing at all — which is what this used to do.
  panel.addEventListener(
    'mousedown',
    (event) => {
      if (shouldPreventSearchPanelBlur(event)) event.preventDefault();
    },
    true,
  );
}

/**
 * @param {string} query
 */
function syncSearchModalQueryState(query) {
  const component = document.querySelector('search-modal-component');
  if (component instanceof HTMLElement) {
    component.classList.toggle('search-modal--has-query', Boolean(query));
  }
}

/**
 * The PLP card's promo price ("$324.00 with code FF20") for hits with the
 * promo tag: same tag match and rounding as snippets/webster-product-card.liquid,
 * with the settings layout/theme.liquid puts on window.wfPromo (absent when the
 * promo is off). Returns the price formatted like the card's, or ''.
 *
 * @param {Record<string, unknown>} hit
 * @param {{ displayPrice?: Function } | undefined} helpers
 * @returns {string}
 */
function hitPromoPrice(hit, helpers) {
  const promo = /** @type {{ tag?: string; percent?: number } | undefined} */ (
    /** @type {any} */ (window).wfPromo
  );
  if (!promo?.tag || !(Number(promo.percent) > 0)) return '';
  if (!Array.isArray(hit.tags) || !hit.tags.includes(promo.tag)) return '';

  const cents = Math.round(Number(hit.price) * 100);
  if (!Number.isFinite(cents) || cents <= 0) return '';
  const promoCents = Math.floor((cents * (100 - Number(promo.percent)) + 50) / 100);

  return helpers?.displayPrice?.({ price: promoCents / 100, currency: hit.currency }, false) ?? '';
}

/**
 * @param {Record<string, unknown>} hit
 * @returns {boolean}
 */
function hitHasNewTag(hit) {
  if (!Array.isArray(hit.tags)) return false;

  return hit.tags.some(
    (tag) => typeof tag === 'string' && tag.toLowerCase() === 'new',
  );
}

/**
 * @param {unknown} value
 * @returns {number | null}
 */
function parseProductTimestamp(value) {
  if (value == null || value === '') return null;

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 1e12 ? value : value * 1000;
  }

  if (typeof value === 'string') {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && /^\d+$/.test(value.trim())) {
      return numeric > 1e12 ? numeric : numeric * 1000;
    }

    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

/**
 * @returns {number}
 */
function getProductNewDaysSetting() {
  // @ts-expect-error Theme settings injected in snippets/scripts.liquid
  const days = Number(window.Theme?.settings?.productNewDays);
  return Number.isFinite(days) && days > 0 ? days : 0;
}

/**
 * @param {Record<string, unknown>} hit
 * @returns {boolean}
 */
function hitIsWithinNewDays(hit) {
  const productNewDays = getProductNewDaysSetting();
  if (productNewDays <= 0) return false;

  const referenceMs =
    parseProductTimestamp(hit.published_at) ??
    parseProductTimestamp(hit.created_at);
  if (referenceMs == null) return false;

  const ageDays = Math.floor((Date.now() - referenceMs) / 86400000);
  return ageDays < productNewDays;
}

/**
 * @param {Record<string, unknown>} hit
 * @returns {boolean}
 */
function hitShowsNewBadge(hit) {
  return hitHasNewTag(hit) || hitIsWithinNewDays(hit);
}

/**
 * Every search hit is a product-level (_distinct) record, and this used to
 * bail out on those, so no search result ever showed a markdown. The record
 * still carries one variant's price and compare_at_price, which is what the
 * PLP card reads too (selected_or_first_available_variant).
 *
 * @param {Record<string, unknown>} hit
 * @returns {boolean}
 */
function hitHasSalePrice(hit) {
  const price = Number(hit.price);
  const compareAt = Number(hit.compare_at_price);
  return Number.isFinite(price) && Number.isFinite(compareAt) && compareAt > price;
}

/**
 * The PLP card's "90% Off", rounded to the nearest 5% with the same integer
 * steps as snippets/webster-product-card.liquid: per-mille off, +25, ÷50, ×5.
 *
 * @param {Record<string, unknown>} hit
 * @returns {number}
 */
function hitDiscountPercent(hit) {
  const price = Math.round(Number(hit.price) * 100);
  const compareAt = Math.round(Number(hit.compare_at_price) * 100);
  if (!(compareAt > price) || price < 0) return 0;

  const perMille = Math.floor(((compareAt - price) * 1000) / compareAt);
  return Math.floor((perMille + 25) / 50) * 5;
}

/**
 * @param {Record<string, unknown>} hit
 * @returns {{ src: string, sizes: string, srcset: string }}
 */
function resolveInstantSearchImage(hit) {
  // @ts-expect-error Algolia app injects this global at runtime
  const helpers = algoliaShopify?.helpers;
  const imageData = helpers?.renderImage?.(hit, 720) ?? {
    src: '',
    sizes: '',
    srcset: '',
  };

  const src =
    imageData.src ||
    (typeof hit.image_url === 'string' ? hit.image_url : '') ||
    (typeof hit.product_image_url === 'string' ? hit.product_image_url : '') ||
    (typeof helpers?.mediumImage === 'function' ? helpers.mediumImage(hit) : '');

  return {
    src,
    sizes: imageData.sizes ?? '',
    srcset: imageData.srcset ?? '',
  };
}

/**
 * @returns {{ label: string, loading: string }}
 */
function getInstantSearchLoadMoreTranslations() {
  const translations =
    /** @type {{ load_more_search_results_label?: string, load_more_search_results_loading?: string } | undefined} */ (
      window.Theme?.translations
    ) ?? {};

  return {
    label:
      translations.load_more_search_results_label ??
      'Load more ([[CUR]]/[[TOT]])',
    loading:
      translations.load_more_search_results_loading ??
      'Loading ([[CUR]]/[[TOT]])',
  };
}

/**
 * @param {string} template
 * @param {number} current
 * @param {number} total
 * @returns {string}
 */
function interpolateInstantSearchLoadMoreTemplate(template, current, total) {
  return String(template ?? '')
    .split('[[CUR]]')
    .join(String(current))
    .split('[[TOT]]')
    .join(String(total));
}

/**
 * @returns {number}
 */
function countInstantSearchHitItems() {
  return document.querySelectorAll(
    '.ais-InfiniteHits-item .ais-hit, .ais-Hits-item .ais-hit',
  ).length;
}

/**
 * @returns {unknown}
 */
function getInstantSearchHelper() {
  // @ts-expect-error Algolia app injects this global at runtime
  return (
    algoliaShopify?.instantsearch?.search?.helper ??
    algoliaShopify?.instant?.search?.helper ??
    algoliaShopify?.search?.helper ??
    null
  );
}

/**
 * @returns {string}
 */
function getInstantSearchStateKey() {
  const helper = /** @type {{ state?: Record<string, unknown> } | null} */ (
    getInstantSearchHelper()
  );
  const state = helper?.state;
  if (!state) return '';

  return JSON.stringify({
    query: state.query,
    filters: state.filters,
    facetFilters: state.facetFilters,
    numericRefinements: state.numericRefinements,
    index: state.index,
  });
}

function resetInstantSearchAccumulatedHits() {
  preservedInstantSearchHitItems = [];
  preservedInstantSearchStateKey = null;
}

function captureInstantSearchHitItemsForAppend() {
  const list = document.querySelector('.ais-Hits-list, .ais-InfiniteHits-list');
  if (!(list instanceof HTMLElement)) return;

  preservedInstantSearchHitItems = [
    ...list.querySelectorAll('.ais-Hits-item, .ais-InfiniteHits-item'),
  ].map((item) => /** @type {HTMLElement} */ (item.cloneNode(true)));

  preservedInstantSearchStateKey = getInstantSearchStateKey();
}

function mergeInstantSearchAccumulatedHits() {
  if (isInstantSearchInfiniteHitsAvailable()) return;

  const stateKey = getInstantSearchStateKey();
  const helper = /** @type {{ getPage?: () => number } | null} */ (
    getInstantSearchHelper()
  );

  if (helper?.getPage?.() === 0 && preservedInstantSearchHitItems.length > 0) {
    resetInstantSearchAccumulatedHits();
    return;
  }

  if (
    preservedInstantSearchHitItems.length === 0 ||
    preservedInstantSearchStateKey !== stateKey
  ) {
    return;
  }

  const list = document.querySelector('.ais-Hits-list, .ais-InfiniteHits-list');
  if (!(list instanceof HTMLElement)) {
    resetInstantSearchAccumulatedHits();
    return;
  }

  for (let index = preservedInstantSearchHitItems.length - 1; index >= 0; index--) {
    const item = preservedInstantSearchHitItems[index];
    if (item instanceof HTMLElement) {
      list.insertBefore(item, list.firstChild);
    }
  }

  resetInstantSearchAccumulatedHits();
}

/**
 * @returns {number}
 */
function getInstantSearchTotalHits() {
  const helper = /** @type {{ lastResults?: { nbHits?: number } } | null} */ (
    getInstantSearchHelper()
  );

  const nbHits = helper?.lastResults?.nbHits;
  return typeof nbHits === 'number' ? nbHits : 0;
}

/**
 * @returns {HTMLButtonElement | null}
 */
function getNativeInstantSearchLoadMoreButton() {
  const button = document.querySelector('.ais-InfiniteHits-loadMore');
  return button instanceof HTMLButtonElement ? button : null;
}

/**
 * @returns {boolean}
 */
function isInstantSearchLoadMoreExhausted() {
  if (isInstantSearchInfiniteHitsAvailable()) {
    return isInstantSearchInfiniteHitsNativeExhausted();
  }

  const helper = /** @type {{ getPage?: () => number, lastResults?: { nbPages?: number, nbHits?: number } } | null} */ (
    getInstantSearchHelper()
  );

  if (!helper?.lastResults) return true;

  const currentPage = helper.getPage?.() ?? 0;
  const totalPages = helper.lastResults.nbPages ?? 0;
  const shown = countInstantSearchHitItems();
  const total = helper.lastResults.nbHits ?? 0;

  return currentPage >= totalPages - 1 || (total > 0 && shown >= total);
}

/**
 * @returns {boolean}
 */
function isInstantSearchInfiniteHitsNativeExhausted() {
  const nativeButton = getNativeInstantSearchLoadMoreButton();
  return !nativeButton || nativeButton.disabled;
}

/**
 * @param {boolean} [loading]
 */
function syncInstantSearchLoadMoreButton(loading = false) {
  const mount = document.querySelector('.ais-load-more');
  if (!(mount instanceof HTMLElement)) return;

  const root = mount.querySelector('.load-more-button');
  const button = mount.querySelector('.load-more-button__btn');
  const label = mount.querySelector('.load-more-button__label');

  if (
    !(root instanceof HTMLElement) ||
    !(button instanceof HTMLButtonElement) ||
    !(label instanceof HTMLElement)
  ) {
    return;
  }

  const translations = getInstantSearchLoadMoreTranslations();
  const shown = countInstantSearchHitItems();
  const total = getInstantSearchTotalHits();
  const progress =
    total > 0 ? Math.min(100, Math.round((shown / total) * 100)) : 0;

  root.style.setProperty('--load-more-progress', `${progress}%`);

  if (loading) {
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    root.classList.add('load-more-button--loading');
    label.textContent = interpolateInstantSearchLoadMoreTemplate(
      translations.loading,
      shown,
      total,
    );
    return;
  }

  button.setAttribute('aria-busy', 'false');
  button.disabled = false;
  root.classList.remove('load-more-button--loading');
  label.textContent = interpolateInstantSearchLoadMoreTemplate(
    translations.label,
    shown,
    total,
  );

  const exhausted =
    total === 0 || shown >= total || isInstantSearchLoadMoreExhausted();
  const wrapper = mount.closest('.ais-load-more-wrapper, .ais-pagination-container');

  if (exhausted) {
    mount.hidden = true;
    if (wrapper instanceof HTMLElement) {
      wrapper.hidden = true;
    }
    if (total > 0 && shown >= total) {
      root.classList.add('load-more-button--complete');
    } else {
      root.classList.remove('load-more-button--complete');
    }
    return;
  }

  mount.hidden = false;
  if (wrapper instanceof HTMLElement) {
    wrapper.hidden = false;
  }
  root.classList.remove('load-more-button--complete');
}

function ensureInstantSearchLoadMoreMount() {
  let mount = document.querySelector('.ais-load-more');
  if (mount instanceof HTMLElement) return mount;

  const paginationContainer = document.querySelector('.ais-pagination-container');
  if (!(paginationContainer instanceof HTMLElement)) return null;

  paginationContainer.innerHTML = '';
  paginationContainer.classList.add('ais-load-more-wrapper');

  mount = document.createElement('div');
  mount.className = 'ais-load-more wf-cg__load-more';
  paginationContainer.appendChild(mount);
  return mount;
}

function initInstantSearchLoadMoreButton() {
  if (!document.querySelector('.ais-page')) return;

  const mount = ensureInstantSearchLoadMoreMount();
  if (!(mount instanceof HTMLElement)) return;

  if (mount.dataset.loadMoreInit !== 'true') {
    mount.dataset.loadMoreInit = 'true';
    mount.innerHTML = `
      <div class="load-more-button" style="--load-more-progress: 0%;">
        <button type="button" class="load-more-button__btn" aria-busy="false">
          <span class="load-more-button__fill" aria-hidden="true"></span>
          <span class="load-more-button__label"></span>
        </button>
      </div>
    `;

    const button = mount.querySelector('.load-more-button__btn');
    if (button instanceof HTMLButtonElement) {
      button.addEventListener('click', () => {
        if (isInstantSearchInfiniteHitsAvailable()) {
          const nativeButton = getNativeInstantSearchLoadMoreButton();
          if (!nativeButton || nativeButton.disabled) {
            syncInstantSearchLoadMoreButton(false);
            return;
          }

          syncInstantSearchLoadMoreButton(true);
          nativeButton.click();
          return;
        }

        const helper = /** @type {{ getPage?: () => number, setPage?: (page: number) => { search: () => void }, lastResults?: { nbPages?: number } } | null} */ (
          getInstantSearchHelper()
        );

        if (!helper?.lastResults || typeof helper.setPage !== 'function') {
          syncInstantSearchLoadMoreButton(false);
          return;
        }

        const currentPage = helper.getPage?.() ?? 0;
        const totalPages = helper.lastResults.nbPages ?? 0;

        if (currentPage >= totalPages - 1) {
          syncInstantSearchLoadMoreButton(false);
          return;
        }

        captureInstantSearchHitItemsForAppend();
        syncInstantSearchLoadMoreButton(true);
        helper.setPage(currentPage + 1).search();
      });
    }
  }

  mergeInstantSearchAccumulatedHits();
  syncInstantSearchLoadMoreButton(false);
}

/**
 * @param {Array<{ type?: string, escapedName?: string, title?: string }>} facets
 * @param {Function} html
 */
function renderInstantSearchFacetsMarkup(facets, html) {
  // @ts-expect-error Algolia app injects this global at runtime
  if (algoliaShopify?.config?.app_blocks_dynamic_widgets_beta_enabled) {
    return html`<div class="ais-dynamic-widgets-container"></div>`;
  }

  return facets.map(
    (facet) => html`
      <div
        class="ais-facet-dropdown-wrapper ais-facet-${facet.type} ais-facet-${facet.escapedName} facets__item wf-search__facet-item"
        data-facet-name="${facet.escapedName}"
      >
        <label
          for="${facet.escapedName}"
          class="ais-range-slider--header ais-facet--header ais-header facets__summary wf-search__facet-trigger"
        >
          <span class="facets__label">${facet.title}</span>
          <span class="svg-wrapper icon-caret icon-animated" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11 5.5L7 9.5L3 5.5" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </label>
        <input
          class="ais-dropdown-checkbox"
          type="checkbox"
          id="${facet.escapedName}"
          name="dropdown"
        />
        <div
          class="ais-facet-${facet.escapedName}-container ais-facet-dropdown-container facets__panel-content wf-search__facet-panel"
        ></div>
      </div>
    `,
  );
}

/**
 * Facet values that arrive in all caps read as shouting in the dropdowns, so
 * they drop to sentence case. Sizes are acronyms (XS, US26, OS) and stay as
 * they come, as do values that already carry their own casing (Acne Studios,
 * alexanderwang). Mirrors snippets/list-filter.liquid.
 *
 * @param {string} label
 * @param {boolean} keepAsIs
 * @returns {string}
 */
function facetLabelCase(label, keepAsIs) {
  const text = String(label ?? '');
  if (keepAsIs) return text;
  if (text !== text.toUpperCase() || text === text.toLowerCase()) return text;
  const lower = text.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/**
 * @param {Record<string, unknown>} params
 * @param {{ type?: string, title?: string, name?: string }} facet
 * @returns {Record<string, unknown>}
 */
function customizeInstantSearchFacetParams(params, facet) {
  if (facet.type === 'slider') {
    return {
      ...params,
      pips: true,
      tooltips: {
        format(/** @type {number} */ value) {
          // @ts-expect-error Algolia app injects this global at runtime
          const formatNumber = algoliaShopify?.helpers?.formatNumber;
          const formatted =
            typeof formatNumber === 'function'
              ? formatNumber(value)
              : String(value);
          return `$${formatted}`;
        },
      },
    };
  }

  if (facet.type !== 'disjunctive' && facet.type !== 'conjunctive') {
    return params;
  }

  // The PLP lists every value in a scrolling panel with no counts and no
  // "Show more"; Algolia's defaults cut the list at 10 behind a button.
  return {
    ...params,
    limit: Math.max(Number(params.limit) || 0, 100),
    showMore: false,
    // The PLP's Shopify filters OR the values within a filter (Black or Navy;
    // two designers). The Algolia app has these set to AND ("conjunctive"),
    // so one pick emptied every other value from the list and two designers
    // could never match. Only list facets reach this point.
    operator: 'or',
    // Size is marked searchable in the Algolia app, which puts a "Search for
    // Size" box at the top of its dropdown. No PLP dropdown has one.
    searchable: false,
    templates: {
      ...(/** @type {Record<string, unknown>} */ (params.templates) ?? {}),
      item(
        /** @type {{ label: string, value: string, count: number, isRefined: boolean }} */ item,
        /** @type {{ html: Function }} */ { html },
      ) {
        const disabled = item.count === 0;
        const isSizeFacet = /size/i.test(
          String(facet.title ?? facet.name ?? ''),
        );
        const label = facetLabelCase(item.label, isSizeFacet);
        const activeClass = item.isRefined
          ? ' facets__inputs-list-item--active'
          : '';
        const disabledClass = disabled
          ? ' facets__inputs-list-item--disabled'
          : '';

        return html`
          <li
            class="ais-RefinementList-item ais-facet--item facets__inputs-list-item${activeClass}${disabledClass}"
          >
            <div class="checkbox checkbox--facet-row">
              <input
                type="checkbox"
                class="checkbox__input ais-RefinementList-checkbox ais-facet--checkbox"
                value="${item.value}"
                checked=${item.isRefined}
                disabled=${disabled}
              />
              <label
                class="checkbox__label ais-RefinementList-label ais-facet--label"
              >
                <span class="checkbox__label-text"
                  >${label}</span
                >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  class="icon-facet-checkmark"
                  aria-hidden="true"
                >
                  <path
                    d="M15.2126 3.79492L15.7185 4.3252L15.8835 4.49805L15.7185 4.6709L6.62183 14.2012L6.44116 14.3906L6.2605 14.2012L1.45874 9.1709L1.2937 8.99805L1.45874 8.8252L1.9646 8.29492L2.14526 8.10547L2.32593 8.29492L6.44019 12.6055L14.8513 3.79492L15.032 3.60547L15.2126 3.79492Z"
                    fill="#C48C5D"
                    stroke="#C48C5D"
                    stroke-width="0.5"
                  />
                </svg>
              </label>
            </div>
          </li>
        `;
      },
    },
  };
}

function setupInstantSearchLoadMoreListeners() {
  if (instantSearchLoadMoreInitialized) return;
  instantSearchLoadMoreInitialized = true;

  document.addEventListener('algolia.instantSearch.afterInit', () => {
    initInstantSearchLoadMoreButton();
  });

  document.addEventListener('algolia.instantSearch.onRender', () => {
    initInstantSearchLoadMoreButton();
  });
}

/**
 * @param {Record<string, unknown>} options
 */
function disableQuerySuggestionsPlugin(options) {
  // @ts-expect-error Algolia app injects this global at runtime
  if (typeof algoliaShopify !== 'undefined' && algoliaShopify.config) {
    // @ts-expect-error Algolia app injects this global at runtime
    algoliaShopify.config.autocomplete_query_suggestions = false;
  }

  // @ts-expect-error Algolia app injects this global at runtime
  const querySuggestionsPlugin = algoliaShopify?.querySuggestionsPlugin;
  if (Array.isArray(options.plugins) && querySuggestionsPlugin) {
    options.plugins = options.plugins.filter(
      (plugin) => plugin !== querySuggestionsPlugin,
    );
  }
}

document.addEventListener('algolia.hooks.initialize', function () {
  // @ts-expect-error Algolia app injects this global at runtime
  if (typeof algoliaShopify === 'undefined' || !algoliaShopify.hooks) return;

  setupInstantSearchLoadMoreListeners();

  // @ts-expect-error Algolia app injects this global at runtime
  algoliaShopify.hooks.registerHook(
    'beforeInstantSearchOptions',
    function (/** @type {Record<string, unknown>} */ options) {
      options.hitsPerPage = INSTANTSEARCH_HITS_PER_PAGE;
      return options;
    },
  );

  // @ts-expect-error Algolia app injects this global at runtime
  algoliaShopify.hooks.registerHook(
    'afterInstantSearchStartRemoveDefaultWidgets',
    function (/** @type {Array<{ $$widgetType?: string }>} */ defaultWidgets) {
      if (isInstantSearchInfiniteHitsAvailable()) {
        return defaultWidgets.filter(
          (widget) =>
            widget.$$widgetType === 'ais.pagination' ||
            widget.$$widgetType === 'ais.hits',
        );
      }

      return defaultWidgets.filter(
        (widget) => widget.$$widgetType === 'ais.pagination',
      );
    },
  );

  // @ts-expect-error Algolia app injects this global at runtime
  algoliaShopify.hooks.registerHook(
    'beforeInstantSearchStartAddWidgets',
    function () {
      if (!isInstantSearchInfiniteHitsAvailable()) return [];

      // @ts-expect-error Algolia app injects this global at runtime
      const infiniteHits = algoliaShopify.externals?.widgets?.infiniteHits;

      return [
        infiniteHits({
          container: '.ais-hits-container',
        }),
      ];
    },
  );

  // @ts-expect-error Algolia app injects this global at runtime
  algoliaShopify.hooks.registerHook(
    'beforeInstantSearchMainTemplate',
    function (
      _defaultTemplate,
      /** @type {{ facets: Array<{ type?: string, escapedName?: string, title?: string }>, multipleSortOrders?: boolean }} */ data,
      /** @type {Function} */ html,
    ) {
      return html`
        <div class="ais-page wf-search wf-cg">
          <h1 class="ais-h2">
            ${window.Theme?.translations?.search_results_title ??
            'Search results'}
          </h1>
          <div class="ais-input wf-search__query">
            <span class="svg-wrapper wf-search__query-icon" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" width="29" height="31" viewBox="0 0 29 31" fill="none">
                <path d="M9.34465 22.9581C11.149 22.9581 12.7944 22.2578 14.0584 21.109L18.7205 26.9161L19.7903 25.7945L14.7862 20.3461C15.8819 19.0209 16.5499 17.2959 16.5499 15.4042C16.5499 11.2323 13.324 7.85034 9.34465 7.85034C5.36529 7.85034 2.1394 11.2323 2.1394 15.4042C2.1394 19.5761 5.36529 22.9581 9.34465 22.9581ZM9.34465 8.92947C12.7502 8.92947 15.5206 11.8339 15.5206 15.4042C15.5206 18.9745 12.7502 21.879 9.34465 21.879C5.93914 21.879 3.16872 18.9745 3.16872 15.4042C3.16872 11.8339 5.93914 8.92947 9.34465 8.92947Z" fill="currentColor"/>
              </svg>
            </span>
            <div class="ais-search-box-container"></div>
            <div class="ais-input-button">
              <div class="ais-clear-input-icon"></div>
            </div>
          </div>
          <div class="ais-facets-button wf-search__legacy-facets-button">
            ${algoliaShopify.translations.showFilters}
          </div>
          <div class="ais-facets wf-search__facets-source">
            <div
              class="ais-clear-refinements-container wf-search__clear-refinements"
            ></div>
            <div
              class="ais-current-refined-values-container wf-search__current-refined"
            ></div>
            ${
              algoliaShopify.config.app_blocks_dynamic_widgets_beta_enabled
                ? html`<div class="ais-dynamic-widgets-container"></div>`
                : renderInstantSearchFacetsMarkup(data.facets, html)
            }
          </div>
          <div class="ais-block wf-search__results-block">
            <div class="ais-search-header wf-search__search-header">
              <div class="ais-stats-container"></div>
              <div class="ais-change-display">
                <span
                  class="ais-change-display-block ais-change-display-selected"
                  ><i class="fa fa-th-large"></i
                ></span>
                <span class="ais-change-display-list"
                  ><i class="fa fa-th-list"></i
                ></span>
              </div>
              <div class="ais-sort wf-search__sort">
                ${
                  data.multipleSortOrders
                    ? html`
                        <span
                          class="ais-sort-orders-container wf-search__sort-orders"
                        ></span>
                      `
                    : ''
                }
              </div>
            </div>
            <div
              class="ais-hits-container ais-results-as-block wf-search__hits"
            ></div>
          </div>
          <div class="ais-pagination-container"></div>
        </div>
      `;
    },
  );

  // @ts-expect-error Algolia app injects this global at runtime
  algoliaShopify.hooks.registerHook(
    'beforeInstantSearchFacetParamsOptions',
    customizeInstantSearchFacetParams,
  );

  // @ts-expect-error Algolia app injects this global at runtime
  algoliaShopify.hooks.registerHook(
    'beforeInstantSearchStatsTemplate',
    function (
      _defaultTemplate,
      /** @type {{ hasOneResult?: boolean, hasManyResults?: boolean, page?: number, hitsPerPage?: number, nbHits?: number }} */ data,
      /** @type {Function} */ html,
    ) {
      return html`
        ${
          data.hasOneResult &&
          html`
            <span class="ais-stats--nb-results"> 1 result found </span>
          `
        }
        ${
          data.hasManyResults &&
          html`
            ${(data.page ?? 0) * (data.hitsPerPage ?? 0) + 1} - ${' '}
            ${Math.min(
              ((data.page ?? 0) + 1) * (data.hitsPerPage ?? 0),
              data.nbHits ?? 0,
            )}
            ${' '}
            out of
            <span class="ais-stats--nb-results">
              ${' '}
              ${algoliaShopify.helpers.formatNumber(data.nbHits ?? 0)} ${' '}
              results found
            </span>
          `
        }
      `;
    },
  );

  // @ts-expect-error Algolia app injects this global at runtime
  algoliaShopify.hooks.registerHook(
    'beforeInstantSearchNoResultTemplate',
    function (_defaultTemplate, /** @type {Function} */ html) {
      return html`
        <div
          class="wf-cg__empty wf-search__empty ais-hit-empty"
          data-testid="empty-state"
        >
          <h2 class="h2 wf-cg__empty-title wf-search__empty-title">
            ${algoliaShopify.translations.noResultFound}
          </h2>
          <p class="wf-search__empty-actions">
            ${algoliaShopify.translations.try}${' '}
            <a class="ais-hit-empty--clear-filters wf-search__empty-link">
              ${algoliaShopify.translations.clearFilters}
            </a>${' '}
            ${algoliaShopify.translations.or}${' '}
            <a class="ais-hit-empty--clear-input wf-search__empty-link">
              ${algoliaShopify.translations.changeInput}
            </a>
          </p>
        </div>
      `;
    },
  );

  // @ts-expect-error Algolia app injects this global at runtime
  algoliaShopify.hooks.registerHook(
    'beforeAutocompleteAsyncFunction',
    async function () {
      await probeQuerySuggestionsIndex();
    },
  );

  // @ts-expect-error Algolia app injects this global at runtime
  algoliaShopify.hooks.registerHook(
    'beforeAutocompleteConfigurationOptions',
    function (
      /** @type {Record<string, unknown>} */ config,
      /** @type {{ sourceId?: string } | undefined} */ context,
    ) {
      if (!shouldUseSearchModalTemplates()) return config;

      if (
        context?.sourceId === 'products' ||
        typeof config.hitsPerPage !== 'number'
      ) {
        config.hitsPerPage = getAutocompleteHitsPerPage();
      }

      return config;
    },
  );

  // @ts-expect-error Algolia app injects this global at runtime
  algoliaShopify.hooks.registerHook(
    'beforeAutocompleteHeaderTemplate',
    function (
      /** @type {unknown} */ _defaultTemplate,
      /** @type {{ html: Function }} */ templateOptions,
      /** @type {string} */ resource,
    ) {
      if (!shouldUseSearchModalTemplates()) return _defaultTemplate;

      const { html } = templateOptions;
      const customHeader = renderAutocompleteSectionHeader(
        html,
        normalizeAutocompleteResource(resource, templateOptions) || resource,
      );

      if (customHeader) return customHeader;

      return _defaultTemplate;
    },
  );

  // @ts-expect-error Algolia app injects this global at runtime
  algoliaShopify.hooks.registerHook(
    'beforeAutocompleteMainProductsTemplate',
    function (
      /** @type {unknown} */ _defaultTemplate,
      /** @type {{ html: Function }} */ templateOptions,
      /** @type {{ products?: unknown }} */ elements,
    ) {
      if (!shouldUseSearchModalTemplates()) return _defaultTemplate;

      const { html } = templateOptions;
      const heading = getSearchModalLabels().products;
      const customHeader = renderAutocompleteSectionHeader(html, 'products');

      return html`
        <div
          class="aa-PanelLayout aa-Panel--scrollable search-modal__products-only"
        >
          <div class="aa-PanelSection">
            ${customHeader ||
            html`
              <div class="aa-SourceHeader search-modal__results-header">
                <span
                  class="aa-SourceHeaderTitle search-modal__results-heading search-modal__results-heading--default"
                >
                  ${heading}
                </span>
              </div>
            `}
            ${elements.products}
          </div>
        </div>
      `;
    },
  );

  // @ts-expect-error Algolia app injects this global at runtime
  algoliaShopify.hooks.registerHook(
    'beforeAutocompleteSuggestionsTemplate',
    function (
      /** @type {unknown} */ _defaultTemplate,
      /** @type {{ html: Function, item: { query?: string }, components: { Highlight: Function } }} */ templateOptions,
    ) {
      if (!shouldUseSearchModalTemplates()) return _defaultTemplate;

      const { html, item, components } = templateOptions;
      const query = item.query?.trim() ?? '';
      const href = buildPopularSearchPageUrl(query);

      return html`
        <a
          class="aa-ItemLink aa-ItemWrapper search-modal__suggestion-link"
          href="${href}"
        >
          <div class="aa-ItemContent search-modal__suggestion-content">
            <div class="aa-ItemContentBody">
              <div class="aa-ItemContentTitle search-modal__suggestion-text">
                ${components.Highlight({ hit: item, attribute: 'query' })}
              </div>
            </div>
          </div>
        </a>
      `;
    },
  );

  // @ts-expect-error Algolia app injects this global at runtime
  algoliaShopify.hooks.registerHook(
    'beforeAutocompleteCollectionsTemplate',
    function (
      /** @type {unknown} */ _defaultTemplate,
      /** @type {{ html: Function, item: Record<string, unknown>, components: { Highlight: Function } }} */ templateOptions,
      /** @type {string} */ itemLink,
    ) {
      if (!shouldUseSearchModalTemplates()) return _defaultTemplate;

      const { html, item, components } = templateOptions;

      return html`
        <a
          class="aa-ItemLink aa-ItemWrapper search-modal__designer-link"
          href="${itemLink}"
        >
          <div class="aa-ItemContent search-modal__designer-content">
            <div class="aa-ItemContentBody">
              <div class="aa-ItemContentTitle search-modal__designer-text">
                ${components.Highlight({ hit: item, attribute: 'title' })}
              </div>
            </div>
          </div>
        </a>
      `;
    },
  );

  // @ts-expect-error Algolia app injects this global at runtime
  algoliaShopify.hooks.registerHook(
    'beforeAutocompleteProductTemplate',
    function (
      /** @type {unknown} */ _defaultTemplate,
      /** @type {{ html: Function, item: Record<string, unknown>, components: { Highlight: Function } }} */ templateOptions,
      /** @type {boolean} */ distinct,
      /** @type {string} */ itemLink,
      /** @type {(item: Record<string, unknown>) => void} */ trackSearchAttribution,
    ) {
      if (!shouldUseSearchModalTemplates()) return _defaultTemplate;

      const { html, item, components } = templateOptions;
      // @ts-expect-error Algolia app injects this global at runtime
      const helpers = algoliaShopify.helpers;
      const imageData = helpers?.renderImage?.(item, 400) ?? {
        src: '',
        sizes: '',
      };
      const imageSrc =
        imageData.src ||
        (typeof item.image_url === 'string' ? item.image_url : '') ||
        (typeof item.product_image_url === 'string'
          ? item.product_image_url
          : '');
      const image = { src: imageSrc, sizes: imageData.sizes ?? '' };
      const vendor = item.vendor || item.product_type || '';
      const titleHighlight = components.Highlight({
        hit: item,
        attribute: 'title',
      });
      const variantAddition =
        helpers?.variantTitleAddition?.(item, distinct) ?? '';
      const price = helpers?.displayPrice?.(item, distinct) ?? '';

      return html`
        <a
          href="${itemLink}"
          class="aa-ItemLink aa-ItemWrapper search-modal__product-card"
          onClick="${() => trackSearchAttribution(item)}"
        >
          <div class="search-modal__product-card-media">
            <img
              class="search-modal__product-card-img"
              src="${image.src}"
              sizes="${image.sizes}"
              alt="${item.title ?? ''}"
              loading="lazy"
            />
          </div>
          <div class="search-modal__product-card-body">
            ${vendor
              ? html`<p class="search-modal__product-card-vendor">${vendor}</p>`
              : ''}
            <p class="search-modal__product-card-title">
              ${titleHighlight}${variantAddition}
            </p>
            <p class="search-modal__product-card-price">${price}</p>
          </div>
        </a>
      `;
    },
  );

  // @ts-expect-error Algolia app injects this global at runtime
  algoliaShopify.hooks.registerHook(
    'beforeAutocompleteFooterTemplate',
    function (
      /** @type {unknown} */ _defaultTemplate,
      /** @type {{ html: Function, state: { query?: string, context?: { nbProducts?: number } } }} */ templateOptions,
    ) {
      if (!shouldUseSearchModalTemplates()) return _defaultTemplate;

      const { html, state } = templateOptions;
      const nbProducts = state.context?.nbProducts ?? 0;
      const query = state.query?.trim() ?? '';

      if (!query || nbProducts <= 0) {
        return html``;
      }

      const href = buildPopularSearchPageUrl(query);
      const label = formatSeeAllResultsLabel(nbProducts);

      return html`
        <div class="aa-footer search-modal__results-footer">
          <a
            class="aa-SeeAllBtn wf-btn wf-btn--black_ghost search-modal__see-all"
            href="${href}"
            >${label}</a
          >
        </div>
      `;
    },
  );

  // @ts-expect-error Algolia app injects this global at runtime
  algoliaShopify.hooks.registerHook(
    'beforeInstantSearchProductTemplate',
    function (
      /** @type {unknown} */ _defaultTemplate,
      /** @type {Record<string, unknown>} */ hit,
      /** @type {Function} */ html,
      /** @type {{ Highlight: Function }} */ components,
    ) {
      // @ts-expect-error Algolia app injects this global at runtime
      const helpers = algoliaShopify.helpers;
      const image = resolveInstantSearchImage(hit);
      const showNewBadge = hitShowsNewBadge(hit);
      const showSalePrice = hitHasSalePrice(hit);
      const showBadgeBlock = showNewBadge;
      const vendor =
        typeof hit.vendor === 'string' ? hit.vendor.toUpperCase() : '';
      const productLink = helpers?.instantsearchLink?.(hit) ?? '#';
      const fullTitle =
        helpers?.fullTitle?.(hit.title, hit._distinct, hit.variant_title) ??
        hit.title ??
        '';
      // On sale, show that variant's own price beside its compare-at, as the
      // PLP does, rather than the product's range.
      const price =
        helpers?.displayPrice?.(hit, showSalePrice ? false : hit._distinct) ??
        '';
      const compareAtPrice = showSalePrice
        ? (helpers?.displayStrikedPrice?.(hit.price, hit.compare_at_price) ??
          '')
        : '';
      const discountPercent = showSalePrice ? hitDiscountPercent(hit) : 0;
      const promoPrice = hitPromoPrice(hit, helpers);
      const titleHighlight = components.Highlight({
        attribute: 'title',
        hit,
      });

      return html`
        <article
          class="wf-webster-product-card wf-webster-product-card--aspect-portrait ais-hit ais-product"
          data-algolia-index="${hit.index}"
          data-algolia-position="${hit.productPosition}"
          data-algolia-queryid="${hit.queryID}"
          data-algolia-objectid="${hit.objectID}"
          data-handle="${hit.handle}"
          data-variant-id="${hit.objectID}"
          data-distinct="${hit._distinct}"
          data-product-id="${hit.id}"
          onClick="${() => helpers?.handleItemClick?.(hit)}"
        >
          <a
            class="wf-webster-product-card__link"
            href="${productLink}"
            title="${fullTitle}"
          >
            <span class="visually-hidden">${fullTitle}</span>
          </a>

          <div class="wf-webster-product-card__media">
            <div class="wf-webster-product-card__media-inner">
              <img
                class="wf-webster-product-card__img ais-hit--picture"
                src="${image.src}"
                srcset="${image.srcset}"
                sizes="${image.sizes}"
                alt="${hit.title ?? ''}"
                loading="lazy"
              />
            </div>

            ${showBadgeBlock
              ? html`
                  <div
                    class="wf-webster-product-card__badges"
                    role="list"
                  >
                    ${showNewBadge
                      ? html`
                          <span
                            class="wf-webster-product-card__badge wf-webster-product-card__badge--new"
                            role="listitem"
                            >${window.Theme?.translations?.product_badge_new ?? 'New'}</span
                          >
                        `
                      : ''}
                  </div>
                `
              : ''}
          </div>

          <div class="wf-webster-product-card__body">
            ${vendor
              ? html`<p class="wf-webster-product-card__vendor">${vendor}</p>`
              : ''}
            <p class="wf-webster-product-card__title">${titleHighlight}</p>
            <div class="wf-webster-product-card__price-row">
              ${showSalePrice && compareAtPrice
                ? html`
                    <span
                      class="wf-webster-product-card__price wf-webster-product-card__price--sale"
                      >${price}</span
                    >
                    <span class="wf-webster-product-card__price-compare"
                      >${compareAtPrice}</span
                    >
                    ${discountPercent > 0
                      ? html`<span
                          class="wf-webster-product-card__price-discount"
                          >${discountPercent}% Off</span
                        >`
                      : ''}
                  `
                : html`<span class="wf-webster-product-card__price">${price}</span>`}
              ${promoPrice
                ? html`<span
                    class="wf-webster-product-card__promo-price${showSalePrice
                      ? ' wf-webster-product-card__promo-price--own-line'
                      : ''}"
                    ><span class="wf-webster-product-card__promo-sep" aria-hidden="true">|</span
                    > ${promoPrice} with code ${/** @type {any} */ (window).wfPromo?.code ?? ''}</span
                  >`
                : ''}
            </div>
          </div>
        </article>
      `;
    },
  );

  // @ts-expect-error Algolia app injects this global at runtime
  algoliaShopify.hooks.registerHook(
    'beforeAutocompleteOptions',
    function (/** @type {Record<string, unknown>} */ options) {
      options.panelContainer = '#algolia-autocomplete-panel';
      searchModalTemplatesEnabled =
        options.panelContainer === '#algolia-autocomplete-panel';
      options.openOnFocus = false;

      const mount = document.getElementById('algolia-autocomplete-input');
      if (mount?.closest('#search-modal')) {
        options.detachedMediaQuery = 'none';
      }

      const placeholder = mount?.dataset.placeholder?.trim();
      if (placeholder) {
        options.placeholder = placeholder;
      }

      const existingClassNames =
        options.classNames &&
        typeof options.classNames === 'object' &&
        !Array.isArray(options.classNames)
          ? /** @type {Record<string, string>} */ (options.classNames)
          : {};

      options.classNames = {
        ...existingClassNames,
        form: 'aa-Form search-modal__algolia-form',
        inputWrapper: 'aa-InputWrapper search-modal__algolia-input-wrapper',
        input: 'aa-Input search-input',
        submitButton: 'aa-SubmitButton search-modal__algolia-submit',
        clearButton: 'aa-ClearButton search-modal__algolia-clear',
      };

      if (!querySuggestionsIndexAvailable) {
        disableQuerySuggestionsPlugin(options);
      }

      const previousOnStateChange = options.onStateChange;
      options.onStateChange = (
        /** @type {{ state: { query?: string } }} */ params,
      ) => {
        const query = params.state.query?.trim() ?? '';
        syncSearchModalQueryState(query);
        if (typeof previousOnStateChange === 'function') {
          previousOnStateChange(params);
        }
      };

      return options;
    },
  );

  preventPanelBlurOnPointerDown();

  const searchModal = document.getElementById('search-modal');
  const dialog = searchModal?.querySelector('dialog');

  if (dialog instanceof HTMLDialogElement && dialog.open) {
    loadPopularSearchSuggestions();
  }
});

document.addEventListener('search-modal:open', function () {
  preventPanelBlurOnPointerDown();

  const searchModal = document.getElementById('search-modal');
  const input = getModalSearchInput(searchModal);

  if (input) {
    requestAnimationFrame(() => {
      input.focus();
    });
  }

  loadPopularSearchSuggestions();
});
